import { api } from "./config";

// ── Receipt / Quotation / Invoice generator ─────────────────────────────────

export async function GetSalesDocumentList({ page = 0, pageSize = 20, sort = {}, docType, status, customerId, search, fromDate, toDate } = {}) {
  try {
    const response = await api().get("/sales-documents/list", {
      params: { page, pageSize, sort: JSON.stringify(sort), docType, status, customerId, search, fromDate, toDate },
    });
    return response.data;
  } catch (error) {
    return { success: false, data: [], message: error?.response?.data?.message || error.message };
  }
}

export async function GetSalesDocumentById(id) {
  try {
    const response = await api().get(`/sales-documents/${id}`);
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

// payload: { docType, date?, customer:{customerId?,name,address?,contact?},
//            items:[{description,qty,price,deliveryFee?,shippingFee?,packageId?}], currency?,
//            termOfPayment?, dueDate?, note?, hideFees?, saveAsDraft? }
export async function CreateSalesDocument(payload) {
  try {
    const response = await api().post("/sales-documents", payload);
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

// Renders the same payload as CreateSalesDocument would, but WITHOUT saving
// anything or consuming a real doc number — for the review-before-confirm
// step in CreateTab.jsx. Axios still routes an error response through
// responseType:"blob", so a validation failure has to be read back out of
// the blob as text before it can be parsed as JSON.
export async function PreviewSalesDocumentPdf(payload) {
  try {
    const response = await api().post("/sales-documents/preview", payload, { responseType: "blob" });
    return { success: true, url: URL.createObjectURL(response.data) };
  } catch (error) {
    const blob = error?.response?.data;
    if (blob instanceof Blob) {
      try {
        const parsed = JSON.parse(await blob.text());
        return { success: false, message: parsed.message };
      } catch {
        // fall through to the generic message below
      }
    }
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

export async function UpdateSalesDocument(id, payload) {
  try {
    const response = await api().patch(`/sales-documents/${id}`, payload);
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

// ── Drafts ──────────────────────────────────────────────────────────────────
// A draft is a saved-but-not-issued document (status "draft", placeholder
// "DRAFT-<id>" number) — see backend/models/SalesDocument.js DRAFT_STATUS.
// Save/update take the same payload as CreateSalesDocument; validation is
// relaxed so a half-filled form can be saved.
export async function SaveSalesDocumentDraft(payload) {
  return CreateSalesDocument({ ...payload, saveAsDraft: true });
}

export async function UpdateSalesDocumentDraft(id, payload) {
  return UpdateSalesDocument(id, { ...payload, saveAsDraft: true });
}

// Issues the draft with the editor's latest state — assigns the real doc
// number, today's date and the latest exchange rate.
export async function IssueSalesDocumentDraft(id, payload) {
  try {
    const response = await api().post(`/sales-documents/${id}/issue`, payload);
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

export async function DiscardSalesDocumentDraft(id) {
  try {
    const response = await api().post(`/sales-documents/${id}/discard`);
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

// Returns the URL to open/print the generated PDF (an authenticated GET —
// the caller opens it via a same-origin fetch->blob, since a plain <a href>
// wouldn't carry the auth cookie cross-context in every browser setup).
export async function GetSalesDocumentPdfBlobUrl(id) {
  try {
    const response = await api().get(`/sales-documents/${id}/pdf`, { responseType: "blob" });
    return { success: true, url: URL.createObjectURL(response.data) };
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

export async function GetCompanyInfo() {
  try {
    const response = await api().get("/sales-documents/company-info");
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}

export async function UpdateCompanyInfo(payload) {
  try {
    const response = await api().patch("/sales-documents/company-info", payload);
    return response.data;
  } catch (error) {
    return { success: false, message: error?.response?.data?.message || error.message };
  }
}
